import { IMultiformDictionary, IMultiformDictionaryElement } from '@/modules/multiForm/shared/IMultiformDictionary';
import { OfferDataTableElementKey } from '@/components/calculator/results/offers/OfferTableElement';
import { LoanFeatures, Offer } from "@/models/simulation/subtypes";
import { SimulationProductType, useSimulationStore } from '@/components/calculator/services/SimulationStore';
import { ProductType } from '@/commons/enums/ProductType';
import { groupBy, map, minBy } from 'lodash-es';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import { useSimulationCurrency } from '@/components/calculator/useSimulationCurrency';
import AuthService from '@/modules/user/AuthService';
import EnvironmentService from '@/env/EnvironmentService';
import type { RequiredInsurances } from '@/models/Demand';
import { useSimulationOfferRowValues } from '@/modules/calculator-v25/composables/shared/results/useSimulationOfferRowValues';
import { useSimulationCombinedCosts } from '@/modules/calculator-v25/composables/shared/results/useSimulationCombinedCosts';
import { useSimulationAmountToRepay } from '@/modules/calculator-v25/composables/shared/results/useSimulationAmountToRepay';

export enum FilterCategory {
  INTEREST_RATE = 'INTEREST_RATE',
  REFERENCE_RATE = 'REFERENCE_RATE',
  OFFER_FEATURES = 'OFFER_FEATURES',
  PROGRAM = 'PROGRAM',
}

interface ISortingMultiformElement<T> extends IMultiformDictionaryElement<T> {
  sortFunction: (a: Offer, b: Offer) => number
}
export interface OfferFilters extends IMultiformDictionaryElement<FilterKey> {
  filterFunction: (offer: Offer) => boolean,
  condition?: () => boolean,
  disabledBy?: FilterKey[],
}
export type IFilteringMultiformElement = {
  [K in FilterCategory]?: OfferFilters[]
}

export type FilterKey = `${keyof LoanFeatures}True` | `${keyof LoanFeatures}False` | string

export type OfferFilter = Record<FilterKey, boolean>

const useOfferSorting = () => {
  const store = useSimulationStore();
  const { isForeignCurrency, } = useSimulationCurrency();
  const {
    getInstallmentAmount,
    getMonthlyCostsAmount,
    getInitialCostsSum,
    getInterestRatePercentage,
    getMargin,
  } = useSimulationOfferRowValues();
  const { getCombinedCostsAmount } = useSimulationCombinedCosts();
  const { getAmountToRepay } = useSimulationAmountToRepay();

  const mortgageProgramFiltersPL: OfferFilters[] = [
    {
      name_pl: I18NGetter().useOfferSorting.NORMAL_OFFERS,
      type: 'noProgramTrue',
      filterFunction: (x: Offer) =>
        !x.features.isHomeStartProgram && !x.features.isRkmProgram && !x.features.isSpecialOffer && !x.features.isEko && !x.features.isSafeCredit2Percent,
    },
    {
      name_pl: I18NGetter().useOfferSorting.HOME_START_OFFERS,
      type: 'isHomeStartProgramTrue',
      // TODO odblokowac po dodaniu ofert
      condition: () => false,
      filterFunction: (x: Offer) => !!x.features.isHomeStartProgram,
    },
    {
      name_pl: I18NGetter().useOfferSorting.RKM_OFFERS,
      type: 'isRkmTrue',
      filterFunction: (x: Offer) => !!x.features.isRkmProgram,
    },
    {
      name_pl: I18NGetter().useOfferSorting.SPECIAL_OFFERS,
      type: 'isSpecialOfferTrue',
      filterFunction: (x: Offer) => !!x.features.isSpecialOffer,
    },
    {
      name_pl: I18NGetter().useOfferSorting.EKO_OFFERS,
      type: 'isEkoTrue',
      filterFunction: (x: Offer) => !!x.features.isEko,
    },
  ];

  const mortgageProgramFiltersRO: OfferFilters[] = [
    {
      name_pl: I18NGetter().useOfferSorting.NORMAL_OFFERS,
      type: 'noProgramTrue',
      filterFunction: (x: Offer) => !x.features.isNewHomeRO,
    },
    {
      name_pl: 'Noua Casă',
      type: 'isNewHomeROTrue', // Ta sama flaga co wyżej, wykorzystywana u rumunów, jako flaga rządowych ofert
      filterFunction: (x: Offer) => !!x.features.isNewHomeRO,
    },
  ];

  const referenceRateTypeI18n = I18NGetter().ReferenceRateType;

  const offerFilters: Record<SimulationProductType, IFilteringMultiformElement> = {
    [ProductType.MORTGAGE]: {
      [FilterCategory.PROGRAM]: EnvironmentService.Environment.isAppDomain().NESTO_RO
        ? mortgageProgramFiltersRO
        : mortgageProgramFiltersPL,
      [FilterCategory.OFFER_FEATURES]: [
        {
          name_pl: I18NGetter().useOfferSorting.ONLY_EKO_OFFERS,
          type: 'isEkoTrue',
          filterFunction: (x: Offer) => !!x.features.isEko,
          condition: () => Boolean(AuthService.User?.isAppDomain.NESTO_RO),
        },
        {
          name_pl: I18NGetter().useOfferSorting.HIDE_EKO_OFFERS,
          type: 'isEkoFalse',
          filterFunction: (x: Offer) => !x.features.isEko,
          condition: () => Boolean(AuthService.User?.isAppDomain.NESTO_RO),
        },
        {
          name_pl: I18NGetter().useOfferSorting.X_SELL_OFFERS,
          type: 'xSellTrue',
          filterFunction: (x: Offer) => !x.features.noXsell,
        },
        {
          name_pl: I18NGetter().useOfferSorting.PROFITABLE_EARLIER_PAYMENT,
          type: 'profitableEarlierPaymentTrue',
          filterFunction: (x: Offer) => !!x.features.profitableEarlierPayment,
        },
        {
          name_pl: I18NGetter().useOfferSorting.CHEAPEST_BANK_OFFERS,
          type: 'onlyCheapestBankOfferTrue',
          filterFunction: (x: Offer) => {
            // Pick the cheapest offer per bank from the offers that are actually visible, not
            // from the whole pool — otherwise a bank whose cheapest offer gets hidden would show
            // no offers at all. "Visible" means: passes the other active filters AND isn't removed
            // by the default "hide branżowe (special) offers unless their filter is on" rule that
            // `allOffers` applies — otherwise a bank whose cheapest offer is a special offer wins
            // here but is stripped later, leaving that bank empty.
            const isSpecialOfferFilterActive = !!store.userInput?.offerFilters['isSpecialOfferTrue'];
            const otherActiveFilters = Object.values(offerFilters[store.productType!]).flat()
              .filter(f => f.type !== 'onlyCheapestBankOfferTrue' && store.userInput?.offerFilters[f.type]);
            const eligibleOffers = (store.simulation?.offers ?? [])
              .filter(o => !o.features?.isSpecialOffer || isSpecialOfferFilterActive)
              .filter(o => otherActiveFilters.every(f => f.filterFunction(o)));
            const offersGroupedByBanks = groupBy(eligibleOffers, 'info.bank');
            const cheapestBankOffers = map(offersGroupedByBanks, (group: Offer[]) => minBy(group, 'totals.costsSum'));
            return cheapestBankOffers.some(o => o?.info.id === x.info.id);
          },
        },
        {
          name_pl: I18NGetter().useOfferSorting.WITH_SALARY,
          type: 'withSalaryTrue',
          filterFunction: (x: Offer) => !!((x.features.withSalary && !x.features.withoutSalary) || (!x.features.withSalary && !x.features.withoutSalary)),
          disabledBy: ['withoutSalaryTrue'],
          condition: () => Boolean(AuthService.User?.isAppDomain.NESTO_RO),
        },
        {
          name_pl: I18NGetter().useOfferSorting.WITHOUT_SALARY,
          type: 'withoutSalaryTrue',
          filterFunction: (x: Offer) => !!((x.features.withoutSalary && !x.features.withSalary) || (!x.features.withSalary && !x.features.withoutSalary)),
          disabledBy: ['withSalaryTrue'],
          condition: () => Boolean(AuthService.User?.isAppDomain.NESTO_RO),
        },
      ],
      [FilterCategory.INTEREST_RATE]: [
        {
          name_pl: I18NGetter().useOfferSorting.CONST_INTEREST,
          type: 'variableInterestFalse',
          filterFunction: (x: Offer) => !x.features.variableInterest,
        },
        {
          name_pl: I18NGetter().useOfferSorting.VARIABLE_INTEREST,
          type: 'variableInterestTrue',
          filterFunction: (x: Offer) => !!x.features.variableInterest,
        },
      ],
      [FilterCategory.REFERENCE_RATE]: [
        {
          name_pl: I18NGetter().useOfferSorting.ONE_MONTH_REFERENCE_RATE,
          type: 'onlyOneMonthWironReferenceRateTrue',
          filterFunction: (x: Offer) => x.interestRate.referenceRateType === "WIBOR 1M",
          condition: () => Boolean(AuthService.User?.isAppDomain.LENDI_PL) && !isForeignCurrency(store.userInput),
        },
        {
          name_pl: I18NGetter().useOfferSorting.THREE_MONTH_REFERENCE_RATE,
          type: 'onlyThreeMonthReferenceRateTrue',
          filterFunction: (x: Offer) => x.interestRate.referenceRateType === "WIBOR 3M",
          condition: () => Boolean(AuthService.User?.isAppDomain.LENDI_PL) && !isForeignCurrency(store.userInput),
        },
        {
          name_pl: I18NGetter().useOfferSorting.SIX_MONTH_REFERENCE_RATE,
          type: 'onlySixMonthReferenceRateTrue',
          filterFunction: (x: Offer) => x.interestRate.referenceRateType === "WIBOR 6M",
          condition: () => Boolean(AuthService.User?.isAppDomain.LENDI_PL) && !isForeignCurrency(store.userInput),
        },
        {
          name_pl: referenceRateTypeI18n['POLSTR 1M'],
          type: 'onlyOneMonthPolstrReferenceRateTrue',
          filterFunction: (x: Offer) => x.interestRate.referenceRateType === "POLSTR 1M",
          condition: () => Boolean(AuthService.User?.isAppDomain.LENDI_PL) && !isForeignCurrency(store.userInput),
        },
        {
          name_pl: referenceRateTypeI18n['POLSTR 3M'],
          type: 'onlyThreeMonthPolstrReferenceRateTrue',
          filterFunction: (x: Offer) => x.interestRate.referenceRateType === "POLSTR 3M",
          condition: () => Boolean(AuthService.User?.isAppDomain.LENDI_PL) && !isForeignCurrency(store.userInput),
        },
        {
          name_pl: referenceRateTypeI18n['POLSTR 6M'],
          type: 'onlySixMonthPolstrReferenceRateTrue',
          filterFunction: (x: Offer) => x.interestRate.referenceRateType === "POLSTR 6M",
          condition: () => Boolean(AuthService.User?.isAppDomain.LENDI_PL) && !isForeignCurrency(store.userInput),
        },
      ],
    },
    [ProductType.CASH]: {
      [FilterCategory.OFFER_FEATURES]: [
        {
          name_pl: I18NGetter().useOfferSorting.ONLY_EKO_OFFERS,
          type: 'isEkoTrue',
          filterFunction: (x: Offer) => !!x.features.isEko,
        },
        {
          name_pl: I18NGetter().useOfferSorting.HIDE_EKO_OFFERS,
          type: 'isEkoFalse',
          filterFunction: (x: Offer) => !x.features.isEko,
        },
        {
          name_pl: I18NGetter().useOfferSorting.FOREIGNERS,
          type: 'forForeignersTrue',
          filterFunction: (x: Offer) => !!x.features.forForeigners,
          condition: () => isExcludedFilter('forForeignersTrue'),
        },
        {
          name_pl: I18NGetter().useOfferSorting.CREDIT_HOLIDAYS,
          type: 'creditHolidaysTrue',
          filterFunction: (x: Offer) => !!x.features.creditHolidays,
          condition: () => isExcludedFilter('creditHolidaysTrue'),
        },
        {
          name_pl: I18NGetter().useOfferSorting.OVERNIGHT_SEPERATION,
          type: 'overnightSeparationTrue',
          filterFunction: (x: Offer) => !!x.features.overnightSeparation,
          condition: () => isExcludedFilter('overnightSeparationTrue'),
        },
        {
          name_pl: I18NGetter().useOfferSorting.NEGOTIATE_TERMS,
          type: 'negotiableTrue',
          filterFunction: (x: Offer) => !!x.features.negotiable,
          condition: () => isExcludedFilter('negotiableTrue'),
        },
        {
          name_pl: I18NGetter().useOfferSorting.JOB_BREAK_ACCEPTED,
          type: 'jobBreakAcceptedTrue',
          filterFunction: (x: Offer) => !!x.features.jobBreakAccepted,
          condition: () => isExcludedFilter('jobBreakAcceptedTrue'),
        },
        {
          name_pl: I18NGetter().useOfferSorting.ONLY_LOCAL_PROCESS_OFFERS,
          type: 'localProcessTrue',
          filterFunction: (x: Offer) => !!x.features.localProcess,
          condition: () => isExcludedFilter('localProcessTrue'),
        },
        {
          name_pl: I18NGetter().useOfferSorting.ONLY_REMOTE_PROCESS_OFFERS,
          type: 'remoteProcessTrue',
          filterFunction: (x: Offer) => !!x.features.remoteProcess,
          condition: () => isExcludedFilter('remoteProcessTrue'),
        },
        {
          name_pl: I18NGetter().useOfferSorting.ONLY_SIMPLIFIED_PROCESS_OFFERS,
          type: 'simplifiedProcessTrue',
          filterFunction: (x: Offer) => !!x.features.simplifiedProcess,
          condition: () => isExcludedFilter('simplifiedProcessTrue'),
        },
        {
          name_pl: I18NGetter().useOfferSorting.WITH_SALARY,
          type: 'withSalaryTrue',
          filterFunction: (x: Offer) => !!((x.features.withSalary && !x.features.withoutSalary) || (!x.features.withSalary && !x.features.withoutSalary)),
          disabledBy: ['withoutSalaryTrue'],
          condition: () => Boolean(AuthService.User?.isAppDomain.NESTO_RO),
        },
        {
          name_pl: I18NGetter().useOfferSorting.WITHOUT_SALARY,
          type: 'withoutSalaryTrue',
          filterFunction: (x: Offer) => !!((x.features.withoutSalary && !x.features.withSalary) || (!x.features.withSalary && !x.features.withoutSalary)),
          disabledBy: ['withSalaryTrue'],
          condition: () => Boolean(AuthService.User?.isAppDomain.NESTO_RO),
        },
      ],
      [FilterCategory.INTEREST_RATE]: [
        {
          name_pl: I18NGetter().useOfferSorting.CONST_INTEREST,
          type: 'fixedInterestTrue',
          filterFunction: (offer: Offer) => !!offer.features.fixedInterest,
        },
        {
          name_pl: I18NGetter().useOfferSorting.VARIABLE_INTEREST,
          type: 'variableInterestTrue',
          filterFunction: (offer: Offer) => !!offer.features.variableInterest,
        },
      ],
    },
  }
  const isExcludedFilter = (key: FilterKey) => {
    return !EnvironmentService.Environment.getAppDomainConfig().cashComparisonHiddenFilters.includes(key);
  }

  const sortingVariantItems: IMultiformDictionary<boolean> = [
    { type: true, name_pl: I18NGetter().useOfferSorting.ASCENDING, },
    { type: false, name_pl: I18NGetter().useOfferSorting.DESCENDING, },
  ];

  const defaultSort: (a: Offer, b: Offer) => number = (a, b) => store.userInput?.isAscending
    ? a.totals.totalSum - b.totals.totalSum
    : b.totals.totalSum - a.totals.totalSum;

  const getComparableMargin = (offer: Offer): number => {
    return offer.changingMargin?.initialValue ?? offer.interestRate.margin
  }

  const commonSortItems: ISortingMultiformElement<OfferDataTableElementKey>[] = [
    {
      type: OfferDataTableElementKey.TOTAL_SUM,
      name_pl: I18NGetter().useOfferSorting.TOTAL_SUM,
      sortFunction: defaultSort,
    },
    {
      type: OfferDataTableElementKey.RRSO,
      name_pl: I18NGetter().useOfferSorting.RRSO,
      sortFunction: (a, b) => store.userInput?.isAscending
        ? a.rrso - b.rrso
        : b.rrso - a.rrso,
    },
    {
      type: OfferDataTableElementKey.INITIAL_COST,
      name_pl: I18NGetter().useOfferSorting.INITIAL_COSTS,
      sortFunction: (a, b) => store.userInput?.isAscending
        ? a.overview.initialCost - b.overview.initialCost
        : b.overview.initialCost - a.overview.initialCost,
    },
  ];

  // Every PL option orders the offers by the headline value of its own offers-table row, so the
  // sorted column always reads top to bottom. Hence the row values, not the engine's own fields.
  const byRowValue = (getValue: (offer: Offer) => Nullable<number>): (a: Offer, b: Offer) => number =>
    (a, b) => store.userInput?.isAscending
      ? (getValue(a) ?? 0) - (getValue(b) ?? 0)
      : (getValue(b) ?? 0) - (getValue(a) ?? 0);

  const mortgageSortItemsPL: ISortingMultiformElement<OfferDataTableElementKey>[] = [
    {
      type: OfferDataTableElementKey.FIRST_INSTALLMENT,
      name_pl: I18NGetter().useOfferSorting.LOAN_INSTALLMENT,
      sortFunction: byRowValue(offer => getInstallmentAmount(offer, store.userInput)),
    },
    {
      type: OfferDataTableElementKey.MONTHLY_COSTS,
      name_pl: I18NGetter().useOfferSorting.MONTHLY_COSTS,
      sortFunction: byRowValue(offer => getMonthlyCostsAmount(offer, store.userInput)),
    },
    {
      type: OfferDataTableElementKey.INITIAL_COST,
      name_pl: I18NGetter().useOfferSorting.INITIAL_COSTS,
      sortFunction: byRowValue(getInitialCostsSum),
    },
    {
      type: OfferDataTableElementKey.COSTS_SUM,
      name_pl: I18NGetter().useOfferSorting.COMBINED_COSTS,
      sortFunction: byRowValue(getCombinedCostsAmount),
    },
    {
      type: OfferDataTableElementKey.TOTAL_SUM,
      name_pl: I18NGetter().useOfferSorting.AMOUNT_TO_REPAY,
      sortFunction: byRowValue(getAmountToRepay),
    },
    {
      type: OfferDataTableElementKey.INTEREST_RATE_SUM,
      name_pl: I18NGetter().useOfferSorting.INTEREST_RATE,
      sortFunction: byRowValue(offer => getInterestRatePercentage(offer, store.userInput)),
    },
    {
      type: OfferDataTableElementKey.MARGIN,
      name_pl: I18NGetter().useOfferSorting.MARGIN,
      sortFunction: byRowValue(offer => getMargin(offer, store.userInput)),
    },
    {
      type: OfferDataTableElementKey.RRSO,
      name_pl: I18NGetter().useOfferSorting.RRSO,
      sortFunction: byRowValue(offer => offer.rrso),
    },
  ];

  const mortgageSortItemsRO: ISortingMultiformElement<OfferDataTableElementKey>[] = [
    ...commonSortItems,
    {
      type: OfferDataTableElementKey.FIRST_INSTALLMENT,
      name_pl: I18NGetter().useOfferSorting.FIRST_INSTALLMENT,
      sortFunction: (a, b) => store.userInput?.isAscending
        ? a.overview.firstInstallment - b.overview.firstInstallment
        : b.overview.firstInstallment - a.overview.firstInstallment,
    },
    {
      type: OfferDataTableElementKey.LAST_INSTALLMENT,
      name_pl: I18NGetter().useOfferSorting.LAST_INSTALLMENT,
      sortFunction: (a, b) => store.userInput?.isAscending
        ? a.overview.lastInstallment - b.overview.lastInstallment
        : b.overview.lastInstallment - a.overview.lastInstallment,
    },
    {
      type: OfferDataTableElementKey.MARGIN,
      name_pl: I18NGetter().useOfferSorting.BANK_MARGIN,
      sortFunction: (a, b) => store.userInput?.isAscending
        ? getComparableMargin(a) - getComparableMargin(b)
        : getComparableMargin(b) - getComparableMargin(a),
    },
  ];

  const sortItems: Record<SimulationProductType, ISortingMultiformElement<OfferDataTableElementKey>[]> = {
    [ProductType.MORTGAGE]: EnvironmentService.Environment.isAppDomain().NESTO_RO
      ? mortgageSortItemsRO
      : mortgageSortItemsPL,
    [ProductType.CASH]: [
      ...commonSortItems,
      {
        type: OfferDataTableElementKey.FIRST_INSTALLMENT,
        name_pl: I18NGetter().useOfferSorting.RATE,
        sortFunction: (a, b) => store.userInput?.isAscending
          ? a.overview.firstInstallment - b.overview.firstInstallment
          : b.overview.firstInstallment - a.overview.firstInstallment,
      },
    ],
  };

  const availableProductsItems: IMultiformDictionaryElement<boolean>[] = [
    {
      name_pl: I18NGetter().useOfferSorting.AVAILABLE,
      type: true,
    },
    {
      name_pl: I18NGetter().useOfferSorting.EXCLUDED,
      type: false,
    },
  ];

  const insuranceItems: IMultiformDictionaryElement<keyof RequiredInsurances>[] = [
    {
      name_pl: I18NGetter().useOfferSorting.REAL_ESTATE_INSURANCE,
      type: 'realEstateInsurance',
    },
    {
      name_pl: I18NGetter().useOfferSorting.LIFE_INSURANCE,
      type: 'lifeInsurance',
    },
  ];

  const extraProductItems: IMultiformDictionaryElement<'acquireCreditCard'>[] = [
    {
      name_pl: I18NGetter().useOfferSorting.ACQUIRE_CREDIT_CARD,
      type: 'acquireCreditCard',
    },
  ];

  const filterVisibilityPredicate = (filter: OfferFilters): boolean => {
    return !filter.condition || filter.condition()
  }

  const filterWithPredicate = (predicate: (...args) => boolean): (filters: OfferFilters[] | undefined) => (OfferFilters[] | []) => {
    return (filters: OfferFilters[] | undefined) => {
      return filters?.filter(predicate) || []
    }
  }

  const getVisibleFilters = filterWithPredicate(filterVisibilityPredicate)

  const getFiltersByCategory = (category: FilterCategory): OfferFilters[] => {
    return offerFilters[store.productType!][category] || []
  }

  const getVisibleFiltersByCategory = (category: FilterCategory): OfferFilters[] => {
    return getVisibleFilters(getFiltersByCategory(category))
  }

  // Reverse-lookup: which category does this filter key belong to. Analytics
  // attaches it to filter events so usage can be grouped by category in PostHog.
  const getFilterCategory = (filterKey: FilterKey): FilterCategory | null => {
    const productType = store.productType
    if (!productType) {
      return null
    }
    const categories = offerFilters[productType]
    const match = (Object.keys(categories) as FilterCategory[]).find(
      (category) => categories[category]?.some((filter) => filter.type === filterKey)
    )
    return match ?? null
  }

  return {
    sortItems,
    defaultSort,
    sortingVariantItems,
    offerFilters,
    availableProductsItems,
    insuranceItems,
    extraProductItems,
    getVisibleFiltersByCategory,
    getFilterCategory,
  };
};

export default useOfferSorting;
